import React, { useEffect, useState, useSyncExternalStore } from "react";
import PropTypes from "prop-types";
import Typography from "../components/ui/Typography";
import Button from "../components/ui/Button";
import ArchiveTasteSearchForm from "../components/ArchiveTasteSearchForm";
import SubpageHeader from "../components/SubpageHeader";
import SiteFooter from "../components/SiteFooter";
import usePageMeta from "../hooks/usePageMeta";
import { handleInternalLinkClick } from "../utils/navigation";
import { slugFromErrorPath, slugToSearchQuery } from "../utils/missingShow";

// 404: an address the site never had. 410: a show taken off the site.
const COPY = {
  404: {
    metaTitle: "Page Not Found | PremiereLens",
    description: "There is no PremiereLens page at this address.",
    heading: "There's no page here",
    body: "The address may be mistyped, or the link may be out of date.",
  },
  410: {
    metaTitle: "Show No Longer Listed | PremiereLens",
    description: "This show has been taken off PremiereLens.",
    heading: "This show is no longer listed",
    body: "We've taken it off PremiereLens, so its page has gone.",
  },
};

const NotFoundPage = ({ status = 404 }) => {
  const copy = COPY[status] || COPY[404];
  usePageMeta({
    title: copy.metaTitle,
    description: copy.description,
    noindex: true,
    ogType: "website",
  });

  // The one prerendered page serves every missing address, so the show asked for
  // is read from the address after mount, which keeps hydration identical.
  const pathname = useSyncExternalStore(() => () => {}, () => window.location.pathname, () => null);
  const slug = slugFromErrorPath(pathname || "");
  const [removedTitle, setRemovedTitle] = useState(null);
  useEffect(() => {
    if (status !== 410 || !slug) return undefined;
    let active = true;
    fetch("/api/removed-shows.json")
      .then((response) => (response.ok ? response.json() : {}))
      .then((titles) => { if (active) setRemovedTitle(titles?.[slug] || null); })
      .catch(() => {});
    return () => { active = false; };
  }, [status, slug]);
  const query = slug ? slugToSearchQuery(slug) : "";
  const heading = removedTitle ? `${removedTitle} is no longer listed` : copy.heading;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SubpageHeader backHref="/" backLabel="Home" />
      <main id="main-content" tabIndex={-1} className="route-shell route-shell--standard">
        <div className="max-w-2xl mx-auto text-center py-16">
          <Typography variant="h1" className="text-foreground mb-4">
            {heading}
          </Typography>
          <Typography variant="body" className="text-muted-foreground mb-8">
            {copy.body}
          </Typography>
          <div className="mx-auto mb-8 max-w-xl text-left">
            <ArchiveTasteSearchForm
              key={query}
              id="not-found-search"
              surface="not-found"
              defaultValue={query}
              semantic={false}
              placeholder="Search the archive"
            />
          </div>
          <Button variant="default" asChild>
            <a href="/" onClick={(event) => handleInternalLinkClick(event, "/")}>
              See what&apos;s new
            </a>
          </Button>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

NotFoundPage.propTypes = {
  status: PropTypes.oneOf([404, 410]),
};

export default NotFoundPage;
